import { motion } from "motion/react";
import { Link } from "react-router";
import {
  Check,
  X,
  ArrowRight,
  Palette,
  Code,
  Shield,
  Zap,
  RefreshCw,
  HeadphonesIcon,
} from "lucide-react";

export function Angebot() {
  const included = [
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Professionelles Design",
      description: "Individuelles, modernes Design für Ihre Marke",
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Moderne Technologie",
      description: "Schnelle, responsive Website mit neuester Technik",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Sicherheit & DSGVO",
      description: "SSL, Datenschutz und DSGVO-Konformität inklusive",
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Performance",
      description: "Optimierte Ladezeiten für beste Nutzererfahrung",
    },
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: "Updates & Wartung",
      description: "Regelmäßige Updates und technische Wartung",
    },
    {
      icon: <HeadphonesIcon className="w-6 h-6" />,
      title: "Support",
      description: "Schneller Support bei Fragen und Problemen",
    },
  ];

  const notIncluded = [
    "E-Commerce-Funktionen",
    "Mehr als 5 Seiten (als Add-on verfügbar)",
    "Individuelle Softwareentwicklung",
    "Logo-Design (als Add-on verfügbar)",
    "regelmäßige Content-Erstellung (als Add-on verfügbar)",
  ];

  const comparison = [
    {
      aspect: "Preis",
      baukasten: "Ab 20 € / Monat",
      agentur: "3.000 - 10.000 € + Wartungskosten",
      mutzur: "500 € + 99 € / Monat zzgl. MwSt.",
    },
    {
      aspect: "Aufwand",
      baukasten: "Hoch (Sie müssen alles selbst machen)",
      agentur: "Gering",
      mutzur: "Gering",
    },
    {
      aspect: "Design",
      baukasten: "Begrenzt",
      agentur: "Individuell (High-End)",
      mutzur: "Individuell",
    },
    {
      aspect: "Wartung",
      baukasten: "-",
      agentur: "Teuer",
      mutzur: "Inklusive",
    },
    {
      aspect: "Support",
      baukasten: "-",
      agentur: "Persönlich",
      mutzur: "Persönlich",
    },
    {
      aspect: "DSGVO",
      baukasten: "Ihre Verantwortung",
      agentur: "Inklusive",
      mutzur: "Inklusive",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl mb-6">
              Was ist im Angebot enthalten?
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Ein strukturierter Prozess, der Ihren Webauftritt schnell, professionell und langfristig gestaltet
            </p>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Auf einen Blick</h2>
            <p className="text-xl text-gray-600">
              Das bekommen Sie für 500 € Grundpreis + 99 € monatlich
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {included.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center text-white mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Standard Package */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Unser Paket</h2>
            <p className="text-xl text-gray-600">
              Perfekt für die meisten Unternehmen
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-12 shadow-xl"
          >
            <p className="mb-6 text-sm font-medium text-gray-500">
              Alle Preise verstehen sich zzgl. gesetzlicher MwSt.
            </p>
            <div className="mb-8">
              <h3 className="text-2xl font-semibold mb-6">
                Was ist standardmäßig enthalten?
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Bis zu 5 Seiten (z.B. Start, Über uns, Leistungen, Kontakt, Impressum)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Responsive Design für alle Geräte</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Kontaktformular inklusive</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Google Maps Integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Cookie-Banner & Datenschutz</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Geschützte Entwurfsansicht vor Bestellung</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Barrierefreiheit nach WCAG 2.2</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">SEO-Grundoptimierung</span>
                </li>
              </ul>
            </div>
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-2xl font-semibold mb-6">Nicht enthalten?</h3>
              <ul className="space-y-4">
                {notIncluded.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-6 h-6 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Vergleich</h2>
            <p className="text-xl text-gray-600">
              Baukasten vs. Agentur vs. mutzur
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-x-auto"
          >
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-6 font-semibold text-lg">Aspekt</th>
                  <th className="text-left py-4 px-6 font-semibold text-lg">Baukasten</th>
                  <th className="text-left py-4 px-6 font-semibold text-lg">Agentur</th>
                  <th className="text-left py-4 px-6 font-semibold text-lg bg-gray-50">
                    mutzur
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-6 font-medium">{row.aspect}</td>
                    <td className="py-4 px-6 text-gray-600">{row.baukasten}</td>
                    <td className="py-4 px-6 text-gray-600">{row.agentur}</td>
                    <td className="py-4 px-6 bg-gray-50 font-medium">
                      {row.mutzur}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl mb-6">
              Überzeugt von unserem Angebot?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Starten Sie jetzt mit Ihrer professionellen Website
            </p>
            <Link
              to="/fragebogen"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 group"
            >
              Jetzt starten
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
